import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { synapseWorkerBridge } from '../lib/SynapseWorkerBridge';
import type { HealthCheckResponse, MetricsResponse } from '../types/worker-messages';

interface WebSocketContextType {
  connected: boolean;
  lastMessage: any;
  metrics: any;
  tasks: any[];
  sendMessage: (message: any) => void;
}

const SynapseWebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const SynapseWebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const metricsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const connectingRef = useRef(false); // Prevent concurrent connection attempts
  const maxReconnectDelay = 60000;

  // Check if Synapse is available via worker
  const checkSynapseHealth = async (): Promise<boolean> => {
    try {
      const res = await synapseWorkerBridge.send<HealthCheckResponse>('health:check', {});
      return res.type === 'response' && res.payload.status === 'healthy';
    } catch {
      return false;
    }
  };

  // Fetch metrics via worker - called only when connected
  const fetchMetrics = async () => {
    try {
      const res = await synapseWorkerBridge.send<MetricsResponse>('metrics:get', {});
      if (res.type === 'response') {
        setMetrics(res.payload);
      }
    } catch {
      // Silent fail
    }
  };

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Connect to WebSocket - stable function, no deps that change
  useEffect(() => {
    let mounted = true;

    const connect = async () => {
      // Prevent concurrent connections
      if (connectingRef.current) return;
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

      connectingRef.current = true;

      // Check health first
      const isAvailable = await checkSynapseHealth();
      if (!isAvailable || !mounted) {
        connectingRef.current = false;
        // Backoff retry when down
        const delay = Math.min(5000 * Math.pow(2, reconnectAttempts.current), maxReconnectDelay);
        reconnectAttempts.current++;
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        if (mounted) {
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
        return;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/synapse/ws/tasks?token=synapse-dev`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!mounted) {
          ws.close();
          return;
        }
        connectingRef.current = false;
        setConnected(true);
        reconnectAttempts.current = 0;

        // Start metrics polling (slower - 30s when WS connected)
        if (metricsIntervalRef.current) clearInterval(metricsIntervalRef.current);
        metricsIntervalRef.current = setInterval(fetchMetrics, 30000);
        fetchMetrics();
      };

      ws.onmessage = (event) => {
        if (!mounted) return;
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);

          if (data.type === 'task_update' || data.task) {
            setTasks(prev => {
              const taskId = data.task?.id || data.id;
              const existingIndex = prev.findIndex(t => t.id === taskId);
              if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = data.task || data;
                return updated;
              }
              return [...prev, data.task || data];
            });
          }
        } catch {
          // Silent
        }
      };

      ws.onerror = () => {
        connectingRef.current = false;
      };

      ws.onclose = () => {
        connectingRef.current = false;
        wsRef.current = null;
        if (mounted) {
          setConnected(false);

          // Stop metrics
          if (metricsIntervalRef.current) {
            clearInterval(metricsIntervalRef.current);
            metricsIntervalRef.current = null;
          }

          // Backoff reconnect
          const delay = Math.min(5000 * Math.pow(2, reconnectAttempts.current), maxReconnectDelay);
          reconnectAttempts.current++;
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      mounted = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (metricsIntervalRef.current) clearInterval(metricsIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []); // Empty deps - run once on mount

  const value: WebSocketContextType = {
    connected,
    lastMessage,
    metrics,
    tasks,
    sendMessage,
  };

  return (
    <SynapseWebSocketContext.Provider value={value}>
      {children}
    </SynapseWebSocketContext.Provider>
  );
};

export const useSynapseWebSocket = () => {
  const context = useContext(SynapseWebSocketContext);
  if (!context) {
    throw new Error('useSynapseWebSocket must be used within SynapseWebSocketProvider');
  }
  return context;
};
